"use client";

import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Stack,
  Chip,
  useTheme,
  alpha,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  IconButton,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SecurityIcon from "@mui/icons-material/Security";
import BusinessIcon from "@mui/icons-material/Business";
import StarIcon from "@mui/icons-material/Star";
import BlockIcon from "@mui/icons-material/Block";
import EmailIcon from "@mui/icons-material/Email";
import GavelIcon from "@mui/icons-material/Gavel";
import InfoIcon from "@mui/icons-material/Info";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import PolicyIcon from "@mui/icons-material/Policy";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

function BannedPage() {
  const theme = useTheme();
  const [emailCopied, setEmailCopied] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText("help@sanduuq.com");
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy email:", err);
    }
  };

  const handleEmailClick = () => {
    window.location.href =
      "mailto:help@sanduuq.com?subject=Account Suspension Appeal&body=Hello Sanduuq Support Team,%0D%0A%0D%0AI am writing to appeal my account suspension. Please review my case.%0D%0A%0D%0AAccount Details:%0D%0A- Email: [Your email address]%0D%0A- Account ID: [If known]%0D%0A%0D%0AThank you for your consideration.%0D%0A%0D%0ABest regards";
  };

  const policyViolations = [
    {
      title: "Fraudulent Activity",
      description:
        "Engaging in fraudulent transactions, money laundering, or other financial crimes.",
    },
    {
      title: "Terms of Service Violations",
      description:
        "Repeated violations of our terms of service or user agreement.",
    },
    {
      title: "Abusive Behavior",
      description:
        "Harassment, threats, or abusive behavior towards other users or staff.",
    },
    {
      title: "Security Violations",
      description:
        "Attempting to compromise platform security or unauthorized access attempts.",
    },
    {
      title: "Regulatory Compliance",
      description:
        "Violations of financial regulations or compliance requirements.",
    },
  ];

  const appealProcess = [
    {
      step: 1,
      title: "Submit Appeal",
      description:
        "Contact our support team with your appeal request and relevant information.",
    },
    {
      step: 2,
      title: "Review Process",
      description:
        "Our compliance team will review your case within 5-7 business days.",
    },
    {
      step: 3,
      title: "Decision",
      description:
        "You will receive a final decision via email with detailed explanation.",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: "url('red_leaves.png')" }}>
      <Grid container sx={{ minHeight: "100vh" }}>
        <Grid item xs={12} lg={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 3, sm: 4, lg: 6 },
            }}
          >
            <Container maxWidth="lg">
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 4,
                  overflow: "visible",
                  bgcolor: "background.paper",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                }}
              >
                <CardContent sx={{ p: { xs: 3, sm: 4, lg: 5 } }}>
                  <Box sx={{ textAlign: "center", mb: 4 }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        mb: 3,
                      }}
                    >
                      <BlockIcon sx={{ fontSize: 40, color: "error.main" }} />
                    </Box>
                    <Typography
                      variant="h4"
                      component="h1"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: "text.primary",
                        fontSize: { xs: "1.75rem", sm: "2rem" },
                      }}
                    >
                      Account Suspended
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        fontSize: "1rem",
                        lineHeight: 1.5,
                      }}
                    >
                      Your Sanduuq account has been suspended due to policy
                      violations
                    </Typography>
                  </Box>

                  <Alert
                    severity="error"
                    icon={<WarningIcon />}
                    sx={{
                      mb: 4,
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        theme.palette.error.main,
                        0.2
                      )}`,
                      "& .MuiAlert-message": {
                        fontSize: "1rem",
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Policy Violation Detected
                    </Typography>
                    Your account has been suspended for violating our Terms of
                    Service and platform policies. This action was taken to
                    protect our community and maintain platform integrity.
                  </Alert>

                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <InfoIcon sx={{ mr: 1, color: "primary.main" }} />
                      Suspension Details
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      }}
                    >
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Suspension Date:
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {new Date().toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Status:
                          </Typography>
                          <Chip
                            label="Permanently Suspended"
                            color="error"
                            size="small"
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Appeal Deadline:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="warning.main"
                          >
                            30 days from suspension
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <Accordion
                      sx={{
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <GavelIcon sx={{ mr: 1, color: "primary.main" }} />
                          <Typography variant="subtitle1" fontWeight={600}>
                            Common Policy Violations
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {policyViolations.map((violation, index) => (
                            <ListItem key={index} sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: "error.main",
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={violation.title}
                                secondary={violation.description}
                                primaryTypographyProps={{
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                }}
                                secondaryTypographyProps={{
                                  fontSize: "0.85rem",
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ContactSupportIcon
                        sx={{ mr: 1, color: "primary.main" }}
                      />
                      Appeal Process
                    </Typography>
                    <Stack spacing={2}>
                      {appealProcess.map((step, index) => (
                        <Paper
                          key={index}
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                bgcolor: "primary.main",
                                color: theme.palette.primary.main,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                flexShrink: 0,
                              }}
                            >
                              {step.step}
                            </Box>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{ mb: 0.5 }}
                              >
                                {step.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.9rem" }}
                              >
                                {step.description}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Submit an Appeal
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 3, lineHeight: 1.6 }}
                    >
                      If you believe this suspension was made in error, you can
                      submit an appeal by contacting our support team. Please
                      include relevant information about your case.
                    </Typography>

                    <Stack spacing={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={<EmailIcon />}
                        onClick={handleEmailClick}
                        sx={{
                          py: 1.75,
                          fontSize: "1rem",
                          fontWeight: 600,
                          boxShadow: `0 4px 12px ${alpha(
                            theme.palette.primary.main,
                            0.3
                          )}`,
                          "&:hover": {
                            boxShadow: `0 6px 20px ${alpha(
                              theme.palette.primary.main,
                              0.4
                            )}`,
                          },
                        }}
                      >
                        Send Appeal Email
                      </Button>

                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                        onClick={handleCopyEmail}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <EmailIcon
                            sx={{ color: "primary.main", fontSize: 20 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            help@sanduuq.com
                          </Typography>
                          <IconButton size="small" sx={{ ml: 1 }} aria-label={emailCopied ? "Email copied" : "Copy email address"}>
                            {emailCopied ? (
                              <CheckCircleIcon
                                sx={{ fontSize: 16, color: "success.main" }}
                              />
                            ) : (
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {emailCopied
                            ? "Email copied to clipboard!"
                            : "Click to copy email address"}
                        </Typography>
                      </Paper>
                    </Stack>
                  </Box>

                  <Alert
                    severity="info"
                    icon={<ScheduleIcon />}
                    sx={{
                      mt: 4,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                      border: `1px solid ${alpha(
                        theme.palette.info.main,
                        0.2
                      )}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, mb: 0.5 }}
                    >
                      Appeal Deadline
                    </Typography>
                    Appeals must be submitted within 30 days of the suspension
                    date. After this period, the suspension becomes final and
                    cannot be appealed.
                  </Alert>

                  <Box sx={{ mt: 4, textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 2 }}
                    >
                      For more information, please review our policies:
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="center"
                      flexWrap="wrap"
                    >
                      <Link
                        href="/terms"
                        sx={{
                          color: "primary.main",
                          fontWeight: 500,
                          fontSize: "0.9rem",
                        }}
                      >
                        Terms of Service
                      </Link>
                      <Link
                        href="/privacy"
                        sx={{
                          color: "primary.main",
                          fontWeight: 500,
                          fontSize: "0.9rem",
                        }}
                      >
                        Privacy Policy
                      </Link>
                      <Link
                        href="/privacy"
                        sx={{
                          color: "primary.main",
                          fontWeight: 500,
                          fontSize: "0.9rem",
                        }}
                      >
                        Community Guidelines
                      </Link>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Container>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default BannedPage;
